import css from "./walletCard.module.css";
import {
  Alert,
  Dialog,
  IconInfo,
  IconLink,
  IconArrowBigRight,
  Text,
} from "@idg-web-platform/pulse";
import { useEffect, useRef, useState } from "preact/hooks";

type DevicePlatform = "apple" | "android" | "desktop";

function detectDevicePlatform(): DevicePlatform {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return "apple";
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return "apple";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

export interface WalletCardLegacyProps {
  walletQrCodeImage?: string;
}

/**
 * Legacy tile rendered when WALLET_QR_SIGNED_URL is OFF. Will be removed in a
 * follow-up MR once rollout is complete and the signed-URL tile is in prod.
 */
export function WalletCardLegacy({ walletQrCodeImage }: WalletCardLegacyProps) {
  const [devicePlatform, setDevicePlatform] = useState<DevicePlatform>("desktop");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [error, setError] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    setDevicePlatform(detectDevicePlatform());
  }, []);

  useEffect(() => {
    if (isDialogOpen) {
      dialogRef.current?.showModal?.();
    } else {
      dialogRef.current?.close?.();
    }
  }, [isDialogOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;
    const handleClose = () => setIsDialogOpen(false);
    dialog?.addEventListener("close", handleClose);
    return () => {
      dialog?.removeEventListener("close", handleClose);
    };
  }, []);

  const handleAppleWallet = () => {
    setError(false);
    window.location.href = "/api/c/club/wallet/apple-pass";
  };

  const handleGoogleWallet = () => {
    setError(false);
    window.location.href = "/api/c/club/wallet/google-pass-url";
  };

  const handleOpenQrCode = () => {
    setIsDialogOpen(true);
  };

  return (
    <div
      className={`${css.walletCard} ${devicePlatform === "desktop" ? css.walletCardClickable : ""}`}
      onClick={devicePlatform === "desktop" ? handleOpenQrCode : undefined}
      onKeyDown={
        devicePlatform === "desktop"
          ? (e: KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleOpenQrCode();
              }
            }
          : undefined
      }
      role={devicePlatform === "desktop" ? "button" : undefined}
      tabIndex={devicePlatform === "desktop" ? 0 : undefined}
    >
      <div className={css.walletCardRow}>
        <div className={css.walletCardHeader}>
          <Text sizeStep={0} fontStyle="bold" removeMargin>
            <span className={css.badge}>Neu</span> Kundenkarte im Wallet speichern
          </Text>
        </div>

        {devicePlatform === "desktop" && (
          <div className={css.desktopLink}>
            <IconLink
              text="QR Code anzeigen"
              href=""
              iconComponent={<IconArrowBigRight />}
              iconPosition="right"
              onClick={(e: Event) => {
                e.preventDefault();
                e.stopPropagation();
                handleOpenQrCode();
              }}
            />
          </div>
        )}
      </div>

      <Text
        text="Speichere deine Kundenkarte im Wallet für einen schnelleren Zugriff im Store."
        sizeStep={0}
        removeMargin
        class={css.description}
      />

      {devicePlatform === "apple" && (
        <div className={css.mobileButtons}>
          <button type="button" className={css.walletButton} onClick={handleAppleWallet}>
            <AppleLogoSvg />
            Zu Apple Wallet hinzufügen
          </button>
        </div>
      )}

      {devicePlatform === "android" && (
        <div className={css.mobileButtons}>
          <button type="button" className={css.walletButton} onClick={handleGoogleWallet}>
            <GoogleLogoSvg />
            Zu Google Wallet hinzufügen
          </button>
        </div>
      )}

      {error && (
        <div className={css.errorAlert}>
          <Alert
            type="warning"
            headline="Fehler"
            description="Das hat leider nicht geklappt. Bitte versuche es später erneut."
            showCloseButton={false}
          />
        </div>
      )}

      <div style={{ display: "contents" }}>
        {isDialogOpen && (
          <Dialog
            ref={dialogRef}
            role="dialog"
            class={css.walletDialog}
            onClose={() => setIsDialogOpen(false)}
          >
            <div className={css.dialogContent}>
              <Text
                text="Kundenkarte im Wallet speichern"
                sizeStep={1}
                fontStyle="bold"
                removeMargin
              />
              <Text
                text="Scanne diesen QR-Code mit deinem Smartphone, um deine Kundenkarte direkt zu deinem Wallet hinzuzufügen."
                sizeStep={0}
                removeMargin
              />
              <div className={css.infoNote}>
                <IconInfo />
                <Text
                  text="Die Wallet Funktion ist nur auf mobilen Geräten verfügbar."
                  sizeStep={-1}
                  removeMargin
                  class={css.infoText}
                />
              </div>
              {walletQrCodeImage && (
                <img
                  src={walletQrCodeImage}
                  className={css.dialogQrCode}
                  alt="QR-Code zum Hinzufügen der Kundenkarte zum Wallet"
                />
              )}
            </div>
          </Dialog>
        )}
      </div>
    </div>
  );
}

function AppleLogoSvg() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function GoogleLogoSvg() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.11A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.11V7.05H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.95l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.29 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}
